"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useState, useEffect } from "react"
import type { Route } from 'next'
import clsx from "clsx"
import { Button } from "@/components/ui/button"
import { LogOut, User } from "lucide-react"

const navItems = [
  { href: "/", label: "Home" },
  { href: "/forms", label: "Forms" },
  { href: "/builder", label: "Builder" },
  { href: "/embeds", label: "Embeds" },
  { href: "/vault", label: "Vault" },
]

type UserData = {
  id: string
  email: string
  name: string | null
  role: string
} | null

export function Navbar() {
  const pathname = usePathname()
  const router = useRouter()
  const [user, setUser] = useState<UserData>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchUser()
  }, [pathname]) // Re-fetch when pathname changes to catch login/logout

  async function fetchUser() {
    try {
      const res = await fetch('/api/auth/me')
      const json = await res.json()
      setUser(json.user)
    } catch (error) {
      console.error('Failed to fetch user:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  async function handleSignOut() {
    try {
      await fetch('/api/auth/signout', { method: 'POST' })
      setUser(null)
      router.push('/')
      router.refresh()
    } catch (error) {
      console.error('Failed to sign out:', error)
    }
  }

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/"
    return pathname.startsWith(href)
  }

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-white/80 backdrop-blur">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
        <Link href="/" className="text-lg font-semibold tracking-tight text-foreground">
          WebhookFormBuilder
        </Link>
        <nav className="hidden items-center gap-2 text-sm font-medium text-muted-foreground md:flex">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href as Route}
              className={clsx(
                "rounded-md px-3 py-2 transition",
                isActive(item.href)
                  ? "bg-primary/10 text-primary"
                  : "hover:text-foreground"
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {loading ? (
            <div className="h-9 w-20 animate-pulse rounded bg-muted" />
          ) : user ? (
            <>
              <div className="hidden items-center gap-2 text-sm font-medium text-muted-foreground md:inline-flex">
                <User className="h-4 w-4" />
                <span>{user.name || user.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </Button>
            </>
          ) : (
            <Link href="/signin" className="hidden text-sm font-medium text-muted-foreground md:inline-flex hover:text-foreground">
              Sign in
            </Link>
          )}
          <Button asChild size="sm">
            <Link href="/builder">Launch builder</Link>
          </Button>
        </div>
      </div>
    </header>
  )
}
